import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { organizerContactList } from "../../../api";
import ExportToExcel from "../../../components/ExportToExcel/ExportToExcel";
import { PulseLoader } from "react-spinners";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import { 
  Users, 
  UserCheck, 
  Mail, 
  Search, 
  Send, 
  CheckSquare, 
  Square,
  RefreshCw 
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  RowSelectionState,
} from "@tanstack/react-table";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";

const UserContactList = () => {
  const { slug } = useParams();
  const [contact, setContactList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { isNightMode } = useNightMode();

  const getContactList = async () => {
    try {
      setIsLoading(true);
      const eventData = localStorage.getItem("eventData");
      if (eventData) {
        const organizerId = JSON.parse(eventData).organizerId;
        const response = await organizerContactList(organizerId);
        if (response) {
          const updatedResponse = response.filter((item: any) => {
            const fName = item.firstName?.toLowerCase() || '';
            const lName = item.lastName?.toLowerCase() || '';
            if (fName.includes('attendee') || lName.includes('attendee')) return false;
            if (!item.firstName?.trim() && !item.lastName?.trim() && !item.email?.trim()) return false;
            return true;
          });

          setContactList(updatedResponse);
        }
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getContactList();
  }, [slug]);

  const getInitials = (firstName?: string, lastName?: string) => {
    const f = firstName ? firstName.charAt(0).toUpperCase() : "";
    const l = lastName ? lastName.charAt(0).toUpperCase() : "";
    return `${f}${l}` || "AU";
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="form-check-input cursor-pointer"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: "fullName",
        header: "FULL NAME",
        accessorFn: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || row.phoneNo || 'Guest Contact',
        cell: ({ row }) => {
          const item = row.original;
          const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || item.phoneNo || 'Guest Contact';
          return (
            <div className="d-flex align-items-center gap-3">
              <div className="contact-avatar">
                {getInitials(item.firstName, item.lastName)}
              </div>
              <div>
                <div className="fw-bold contact-name-text fs-13px">{fullName}</div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "EMAIL ADDRESS",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.email || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: "phoneNo",
        header: "PHONE NO",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.phoneNo || 'N/A'}
          </span>
        ),
      },
      {
        id: "location",
        header: "LOCATION",
        cell: ({ row }) => {
          const item = row.original;
          const locationParts = [item.address, item.city, item.state, item.country].filter(Boolean).join(', ');
          return (
            <span className="contact-sub-text fs-12px">
              {locationParts || 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: "zipCode",
        header: "ZIP CODE",
        cell: ({ row }) => (
          <span className="font-mono contact-sub-text fs-12px">
            {row.original.zipCode || 'N/A'}
          </span>
        ),
      },
    ],
    []
  );

  const selectedCount = Object.keys(rowSelection).filter(k => rowSelection[k]).length;
  const isAllSelected = contact.length > 0 && selectedCount === contact.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setRowSelection({});
    } else {
      const newSel: RowSelectionState = {};
      contact.forEach((c, idx) => {
        const id = c.contactId || c.id || `c-${idx}`;
        newSel[id] = true;
      });
      setRowSelection(newSel);
    }
  };

  const sendEmailToSelected = () => {
    const selectedIds = Object.keys(rowSelection).filter(k => rowSelection[k]);
    const selectedEmails = contact
      .filter((c, idx) => selectedIds.includes(c.contactId || c.id || `c-${idx}`))
      .map(c => c.email)
      .filter(Boolean);

    if (selectedEmails.length === 0) {
      alert("No valid email addresses found.");
      return;
    }

    const mailto = `mailto:?bcc=${selectedEmails.join(",")}&subject=From Eventox&body=Check Out Eventox,`;
    window.location.href = mailto;
  };

  const contactsWithEmailCount = useMemo(
    () => contact.filter(c => !!c.email).length,
    [contact]
  );

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />

      {/* Page Header Component */}
      <PageHeader
        title="Venue Contact List"
        subtitle="Monitor real-time event contacts, manage list selections, and export registry sheets."
        isLoading={isLoading}
        actions={
          <>
            <ExportToExcel 
              data={contact} 
              fileName="venue_contact_list" 
              buttonText="Export Attendee Sheet (.CSV)"
            />
          </>
        }
      />

      {/* Stat Cards Grid */}
      <div className="contact-stats-grid">
        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">TOTAL REGISTRY SIZE</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : contact.length} <span className="fs-14px fw-normal contact-sub-text">contacts</span>
            </div>
          </div>
          <div className="contact-stat-icon pink">
            <Users size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">SELECTED CONTACTS</div>
            <div className="contact-stat-value">
              {selectedCount} <span className="fs-14px fw-normal contact-sub-text">selected</span>
            </div>
          </div>
          <div className="contact-stat-icon orange">
            <UserCheck size={22} />
          </div>
        </div>

        <div className="contact-stat-card">
          <div>
            <div className="contact-stat-label">EMAIL DESTINATIONS</div>
            <div className="contact-stat-value">
              {isLoading ? "..." : contactsWithEmailCount} <span className="fs-14px fw-normal contact-sub-text">valid emails</span>
            </div>
          </div>
          <div className="contact-stat-icon green">
            <Mail size={22} />
          </div>
        </div>
      </div>

      {/* Unified Search & Action Bar */}
      <div className="contact-filter-card">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          
          <div className="position-relative flex-grow-1 w-100">
            <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
            <input
              type="text"
              className="contact-search-input"
              placeholder="Filter by name, email or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="contact-action-btn"
              onClick={handleToggleSelectAll}
              disabled={isLoading || contact.length === 0}
            >
              {isAllSelected ? (
                <>
                  <CheckSquare size={16} className="text-pink" /> Unselect All
                </>
              ) : (
                <>
                  <Square size={16} /> Select All
                </>
              )}
            </button>

            {selectedCount > 0 && (
              <button
                type="button"
                className="contact-action-btn primary"
                onClick={sendEmailToSelected}
              >
                <Send size={15} /> Send Email ({selectedCount})
              </button>
            )}
          </div>

        </div>
      </div>

      {/* TanStack Table Card via TablesV2 */}
      <TablesV2
        data={contact}
        columns={columns}
        isLoading={isLoading}
        searchQuery={searchQuery}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row, idx) => row.contactId || row.id || `c-${idx}`}
        loadingMessage="Loading Contact Registry..."
        emptyMessage="No contacts found matching your current search criteria."
      />
    </div>
  );
};

export default UserContactList;
